import React from "react";
import { faMessage } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFormContext, useWatch } from "react-hook-form";
import { Header, Grid, SemanticCOLORS, Segment, Label } from "semantic-ui-react";
import useInspection from "../../../../hooks/inspections/useInspection";
import { IScheduleInspectionModalForm } from "../ScheduleInspectionModal";
import {
  Time,
  CalendarDate,
  parseDate,
  isEqualDay,
  isEqualMonth,
  isEqualYear,
  getLocalTimeZone,
} from "@internationalized/date";

import { formatDate } from "date-fns";

interface Preference {
  date: string;
  meridiem: "AM" | "PM" | null;
}

export default function InspectionRequestedDates({ id }: { id: string }) {
  const { inspectionQuery } = useInspection({ id: id });

  const { setValue } = useFormContext<IScheduleInspectionModalForm>();

  const inspectionDate = useWatch<CalendarDate | null>({ name: "inspectionDate" });
  const inspectionTimeOrMeridiem = useWatch<Time | "AM" | "PM" | null>({ name: "inspectionTime" });

  const request = inspectionQuery.data?.data.attributes.request;
  const preferences =
    request?.preferences
      .slice(0, 3)
      .filter((preference): preference is Preference => {
        return !!preference.date;
      })
      .map((preference) => {
        return {
          date: parseDate(formatDate(new Date(preference.date), "yyyy-MM-dd")),
          meridiem: preference.meridiem,
        };
      }) ?? [];
  const message = request?.message;

  const handleClearRequestedDate = (): void => {
    setValue("inspectionDate", null);
    setValue("inspectionTime", null);
  };

  const handleSelectRequestedDate = (date: CalendarDate | null): void => {
    setValue("inspectionDate", date);
  };

  const handleSelectRequestedMeridiem = (meridiem: "AM" | "PM" | null): void => {
    setValue("inspectionTime", meridiem);
  };

  const dateYearMonthDayEqual = (date: CalendarDate, other: CalendarDate) => {
    return isEqualDay(date, other) && isEqualMonth(date, other) && isEqualYear(date, other);
  };

  return (
    <>
      <Header className='tw-font-serif tw-text-base tw-font-medium'>REQUESTED DATES</Header>
      <Grid stackable className='tw-mb-1 tw-flex tw-justify-between'>
        {preferences.map((preference, index) => {
          const preferenceDate = preference.date;
          const preferenceMeridiem = preference.meridiem;

          const isDateSelected = !!inspectionDate && dateYearMonthDayEqual(preferenceDate, inspectionDate);
          const isAmSelected =
            !inspectionTimeOrMeridiem ||
            inspectionTimeOrMeridiem === "AM" ||
            (inspectionTimeOrMeridiem !== "PM" && inspectionTimeOrMeridiem.hour < 12);
          const isPmSelected =
            !inspectionTimeOrMeridiem ||
            inspectionTimeOrMeridiem === "PM" ||
            (inspectionTimeOrMeridiem !== "AM" && inspectionTimeOrMeridiem.hour >= 12);
          const isMeridiemSelected =
            !inspectionTimeOrMeridiem ||
            (preferenceMeridiem === "AM" && isAmSelected) ||
            (preferenceMeridiem === "PM" && isPmSelected);
          const selected = isDateSelected && isMeridiemSelected;

          return (
            <Grid.Column key={index.toString()} className='tw-grow' width={5}>
              <InspectionPreferenceCard
                onClick={() => {
                  if (isDateSelected && isMeridiemSelected) {
                    handleClearRequestedDate();
                  } else {
                    handleSelectRequestedDate(preferenceDate);
                    handleSelectRequestedMeridiem(preferenceMeridiem);
                  }
                }}
                date={preferenceDate}
                meridiem={preferenceMeridiem}
                choice={index + 1}
                selected={selected}
              />
            </Grid.Column>
          );
        })}
      </Grid>
      {message && (
        <section className='subText tw-mb-2 tw-flex tw-items-center tw-text-sm'>
          <FontAwesomeIcon className='tw-mr-2' icon={faMessage} />
          <p className='tw-mb-0'>{message}</p>
        </section>
      )}
    </>
  );
}

interface IInspectionPreferenceCard {
  selected: boolean;
  date: CalendarDate;
  meridiem: string | null;
  choice: number;
  onClick: () => void;
}
function InspectionPreferenceCard({ date, meridiem, choice, onClick, selected }: IInspectionPreferenceCard) {
  const requestedDateObject = date.toDate(getLocalTimeZone());

  const dateString = requestedDateObject.toLocaleDateString("en-us", {
    year: "numeric",
    day: "numeric",
    month: "short",
  });
  const dayOfWeek = requestedDateObject.toLocaleDateString("en-us", { weekday: "long" });

  const choiceText: Record<number, string> = {
    1: "1st",
    2: "2nd",
    3: "3rd",
  } as const;

  const choiceColor: Record<number, SemanticCOLORS> = {
    1: "orange",
    2: "blue",
    3: "grey",
  };

  const selectedStyles = selected ? "tw-ring-1 tw-ring-blue tw-bg-[#dbeafe]" : "";

  return (
    <Segment
      className={`tw-m-0 hover:tw-cursor-pointer hover:tw-ring-2 hover:tw-ring-blue hover:tw-ring-offset-2 ${selectedStyles}`}
      onClick={onClick}
    >
      {/* I had to use come CSS hacks to get the date to be positioned inline with the ribbon correctly. */}
      <div className='tw-relative'>
        <Label ribbon='right' color={choiceColor[choice]}>
          {choiceText[choice]} Choice
        </Label>
        <span className='tw-absolute tw-left-0 tw-top-0 tw-text-lg tw-font-bold'>{dateString}</span>
      </div>
      <div className='tw-mt-1 tw-flex tw-justify-between tw-text-lg'>
        <div>{dayOfWeek}</div>
        <div>{meridiem ?? "Anytime"}</div>
      </div>
    </Segment>
  );
}
